import React, { useState } from "react";
import { Button, Iconography } from "@hid-galaxy-ui/galaxy-react";
import {
  BenefitCategoryType,
  BenefitItemType,
  BenefitTierType,
} from "../../../../../../../../redux/slices/partner-program/benefits/Types";

type Props = {
  category: BenefitCategoryType;
  tierDetails: BenefitTierType[];
  isEditing: boolean;
  onToggleCollapse: (categoryId: string) => void;
  onDeleteCategory: (categoryId: string, categoryName: string) => void;
  onUpdateTitle: (categoryId: string, value: string) => void;
  onOpenAddBenefit: (categoryId: string) => void;
  onOpenEditBenefit: (categoryId: string, benefitId: string) => void;
  onOpenDescription: (categoryId: string, benefitId: string) => void;
  onToggleTier: (
    categoryId: string,
    benefitId: string,
    tierId: number
  ) => void;
  onDeleteBenefit: (
    categoryId: string,
    benefitId: string,
    benefitName: string,
    benefitApiId?: number
  ) => void;
  onReorder: (categoryId: string, fromIndex: number, toIndex: number) => void;
};

const BenefitsCategoryCard: React.FC<Props> = ({
  category,
  tierDetails,
  isEditing,
  onToggleCollapse,
  onDeleteCategory,
  onUpdateTitle,
  onOpenAddBenefit,
  onOpenEditBenefit,
  onOpenDescription,
  onToggleTier,
  onDeleteBenefit,
  onReorder,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (
    event: React.DragEvent<HTMLSpanElement>,
    index: number
  ) => {
    setDraggedIndex(index);
    event.dataTransfer.effectAllowed = "move";
    // Required by some browsers/webviews for drag/drop to activate.
    event.dataTransfer.setData("text/plain", String(index));
  };

  const handleDrop = (
    event: React.DragEvent<HTMLTableRowElement>,
    targetIndex: number
  ) => {
    event.preventDefault();
    const sourceFromDataTransfer = Number(event.dataTransfer.getData("text/plain"));
    const sourceIndex =
      draggedIndex !== null
        ? draggedIndex
        : Number.isNaN(sourceFromDataTransfer)
          ? null
          : sourceFromDataTransfer;
    if (sourceIndex === null || sourceIndex === targetIndex) return;
    onReorder(category.id, sourceIndex, targetIndex);
    setDraggedIndex(null);
  };

  const isTierActive = (row: BenefitItemType, tier: BenefitTierType, tierIndex: number) => {
    if (row.activeTierIds.includes(tier.ID)) return true;
    // Backward compatibility for APIs returning 1-based positional tier IDs.
    return row.activeTierIds.includes(tierIndex + 1);
  };

  const renderTierCell = (
    row: BenefitItemType,
    tier: BenefitTierType,
    tierIndex: number
  ) => {
    const checked = isTierActive(row, tier, tierIndex);
    if (isEditing) {
      return (
        <input
          className="benefits-tier-checkbox"
          type="checkbox"
          checked={checked}
          onChange={() => onToggleTier(category.id, row.id, tier.ID)}
        />
      );
    }

    return checked ? (
      <span className="benefits-tier-dot" />
    ) : (
      <span className="benefits-tier-empty" />
    );
  };

  return (
    <div className="benefits-category-card">
      <div className="benefits-category-header">
        <button
          className="benefits-collapse-btn"
          onClick={() => onToggleCollapse(category.id)}
          type="button"
          aria-label={category.isCollapsed ? "Expand category" : "Collapse category"}
        >
          <span className={category.isCollapsed ? "chevron collapsed" : "chevron"}>
            <Iconography icon="angleDown" />
          </span>
        </button>

        {isEditing ? (
          <input
            className="benefits-category-input"
            value={category.title}
            onChange={(event) => onUpdateTitle(category.id, event.target.value)}
          />
        ) : (
          <h3>{category.title}</h3>
        )}

        <div className="benefits-category-actions">
          {isEditing ? (
            <>
              <Button
                variant="secondary"
                label="ADD BENEFIT"
                icon={<Iconography icon="plus" />}
                className="benefits-secondary-action"
                onClick={() => onOpenAddBenefit(category.id)}
              />
              <Button
                variant="secondary"
                label="DELETE"
                icon={<Iconography icon="trashCan" />}
                className="benefits-secondary-action"
                onClick={() => onDeleteCategory(category.id, category.title)}
              />
            </>
          ) : null}
        </div>
      </div>

      {!category.isCollapsed && (
        <div className="benefits-table-wrapper">
          <table>
            <thead>
              <tr>
                {isEditing && <th className="drag-col" />}
                <th className="benefits-col">Benefits</th>
                <th className="benefit-type-col">Benefit Type</th>
                <th className="description-col">Description</th>
                {tierDetails.map((tier) => (
                  <th key={tier.ID} className="tier-col">{tier.tier_name}</th>
                ))}
                {isEditing && <th className="action-col">Action</th>}
              </tr>
            </thead>
            <tbody>
              {category.benefits.map((benefit: BenefitItemType, index: number) => (
                <tr
                  key={benefit.id}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => handleDrop(event, index)}
                >
                  {isEditing && (
                    <td className="drag-col">
                      <span
                        className="drag-handle"
                        draggable={true}
                        onDragStart={(event) => handleDragStart(event, index)}
                        onDragEnd={() => setDraggedIndex(null)}
                        title="Drag to reorder"
                        aria-label="Drag to reorder"
                        role="button"
                      >
                        <span className="drag-dots-icon" aria-hidden="true" />
                      </span>
                    </td>
                  )}
                  <td className="benefits-col">{benefit.name}</td>
                  <td className="benefit-type-col">{benefit.benefitsType || "—"}</td>
                  <td className="description-col">
                    <Button
                      variant="tertiary"
                      label="View"
                      className="benefits-link-btn"
                      onClick={() => onOpenDescription(category.id, benefit.id)}
                    />
                  </td>
                  {tierDetails.map((tier, tierIndex) => (
                    <td key={`${benefit.id}-${tier.ID}`} className="tier-col">
                      {renderTierCell(benefit, tier, tierIndex)}
                    </td>
                  ))}
                  {isEditing && (
                    <td className="action-col">
                      <div className="benefits-row-actions">
                        <Button
                          variant="tertiary"
                          label=""
                          icon={<Iconography icon="pen" />}
                          className="benefits-delete-row-btn"
                          onClick={() => onOpenEditBenefit(category.id, benefit.id)}
                          aria-label="Edit benefit row"
                        />
                        <Button
                          variant="tertiary"
                          label=""
                          icon={<Iconography icon="trashCan" />}
                          className="benefits-delete-row-btn"
                          onClick={() =>
                            onDeleteBenefit(
                              category.id,
                              benefit.id,
                              benefit.name,
                              benefit.apiId
                            )
                          }
                          aria-label="Delete benefit row"
                        />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BenefitsCategoryCard;
